<script setup lang="ts">
    import { onMounted, reactive } from 'vue';
    import { bsAlert, AlertTypes } from '@shared/components/BsAlerts';
    import { listInvoices, resolveInvoice, Invoice } from './Billing.access';

    const state = reactive<{
        invoices: Invoice[];
        loading: boolean;
    }>({
        invoices: [],
        loading: false,
    });

    function formatDate(dateStr: string | null): string {
        if (!dateStr) return '';
        return new Date(dateStr).toLocaleDateString();
    }

    function recipientDisplay(inv: Invoice): string {
        return inv.RecipientName || inv.CompanyName;
    }

    async function fetch(): Promise<void> {
        state.loading = true;
        try {
            state.invoices = await listInvoices(4, '', '', '', null, null, null);
        } catch {
            bsAlert({ type: AlertTypes.Danger, title: 'Error', content: 'Failed to load unresolved invoices' });
        } finally {
            state.loading = false;
        }
    }

    async function resolve(inv: Invoice): Promise<void> {
        if (!confirm('Are you sure you want to mark this as paid?')) return;
        try {
            await resolveInvoice(inv.InvoiceId);
            state.invoices = state.invoices.filter((i) => i.InvoiceId !== inv.InvoiceId);
            bsAlert({ type: AlertTypes.Success, title: 'Success', content: 'Invoice resolved' });
        } catch {
            bsAlert({ type: AlertTypes.Danger, title: 'Error', content: 'Failed to resolve invoice' });
        }
    }

    onMounted(fetch);
</script>

<template>
    <div v-if="state.loading">Loading...</div>

    <div class="table-responsive">
        <table class="table table-hover table-condensed">
            <thead class="small">
                <tr>
                    <th>Invoice no</th>
                    <th>Company</th>
                    <th>Due date</th>
                    <th class="text-right">Amount outstanding</th>
                    <th class="text-right">Paid</th>
                    <th>Status</th>
                    <th>Paid date</th>
                    <th></th>
                </tr>
            </thead>
            <tbody>
                <tr v-for="inv in state.invoices" :key="inv.InvoiceId">
                    <td>{{ inv.InvoiceNumber }}</td>
                    <td>{{ recipientDisplay(inv) }}</td>
                    <td>{{ formatDate(inv.DueDate) }}</td>
                    <td class="text-right">{{ inv.CurrencyCode }}</td>
                    <td class="text-right">
                        <span v-if="inv.AmountPaid !== 0">Paid: {{ inv.AmountPaid }} {{ inv.CurrencyCode }}</span>
                    </td>
                    <td>{{ inv.Status }}</td>
                    <td>{{ formatDate(inv.PaidDate) }}</td>
                    <td>
                        <button type="button" class="btn btn-warning btn-xs" @click="resolve(inv)">Resolve payment</button>
                    </td>
                </tr>
            </tbody>
        </table>
    </div>
</template>

<script lang="ts">
    export default { name: 'BillingUnresolved' };
</script>

<style></style>
